import { useEffect } from 'react';

import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';

import useUsersTeamsData from '../../hooks/useUsersTeamsData';
import { type Product } from '../../types';
import { startMeasure, stopMeasure } from '../../utils/performance';
import { triggerAnalyticsForFetchingData, triggerAnalyticsForPreFetchData } from '../analytics';

interface Props {
	cloudId: string;
	orgId?: string;
	userId: string;
	product: Product;
	tenantUrl?: string;
}

// A dummy component is rendered to trigger to pre-fetch users and teams data.
export function UsersTeamsPreFetchInternal({
	cloudId,
	orgId,
	userId,
	product,
	tenantUrl,
	createAnalyticsEvent,
}: Props & WithAnalyticsEventsProps) {
	const { fireEvent } = useAnalyticsEventsNext();
	useEffect(() => {
		if (fg('ptc-enable-teams-common-analytics-refactor')) {
			fireEvent('track.preFetchData.triggered', {});
		} else {
			triggerAnalyticsForPreFetchData(createAnalyticsEvent);
		}
		// measure duration time of fetching users and teams data
		startMeasure('fetchingData');
	}, [createAnalyticsEvent, fireEvent]);

	const { isLoading } = useUsersTeamsData(cloudId, userId, product, tenantUrl, undefined, orgId);

	// stop to measure fetching data time when isLoading is finished.
	useEffect(() => {
		if (!isLoading) {
			stopMeasure('fetchingData', (duration, startTime) => {
				if (fg('ptc-enable-teams-common-analytics-refactor')) {
					fireEvent('track.fetchingUsersTeamsData.measured', {
						duration,
						startTime,
					});
				} else {
					triggerAnalyticsForFetchingData(createAnalyticsEvent, duration, startTime);
				}
			});
		}
	}, [createAnalyticsEvent, isLoading, fireEvent]);

	return null;
}

export const UsersTeamsPreFetch = componentWithFG(
	'ptc-enable-teams-common-analytics-refactor',
	UsersTeamsPreFetchInternal,
	withAnalyticsEvents()(UsersTeamsPreFetchInternal),
);
